import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Button, Typography } from '@mui/material';
import AddRemoveIcon from '@sharedCR/common/AddRemoveIcon';
import DataDisplay, {
  useResponsiveDataDisplayVariant,
} from '@sharedCR/coreDesignSystem/DataDisplay';
import PropTypes from 'prop-types';

import { COLOR_PALETTE } from 'constants/ui';
import useTruncatedGeneral from 'hooks/useTruncatedGeneral';

import {
  selectFinancialInfoCashToClose,
  selectFinancialInfoCurrentFinancialsBorrowerAssets,
  selectFinancialInfoCurrentFinancialsBorrowerIncomes,
  selectFinancialInfoCurrentFinancialsBorrowerLiabilities,
  selectFinancialInfoDebtToIncome,
  selectFinancialInfoProjectedReserves,
  selectFinancialInfoSummaryPendingFields,
  selectFinancialInfoTotalAssets,
  selectFinancialInfoTotalIncomes,
  selectFinancialInfoTotalLiabilities,
  selectFinancialInfoTotalNegativeRentalIncome,
} from 'store/loanApplications/selectors';

import { getSummaryInfoConfig } from '../../../FinancialInfoHead/FinancialInfoHead.config';
import { getSummaryWrapperStyles } from '../../../FinancialInfoHead/FinancialInfoHead.styles';
import { TITLE_DATA } from '../Content.constants';

import { addButtonStyles, titleStyles, titleWrapperStyles } from './AdditionalComponents.styles';
import { AdditionalComponentsTestIds } from './AdditionalComponents.testIds';
import { SUMMARY_CARDS_COLUMNS } from './Title.constants';

const Title = ({ tablePath, onAddNewRecord, pendingChanges }) => {
  const { entityId: borrowerId } = useParams();

  const [onMouseOver, onResetTooltipState, TruncatedProvider] = useTruncatedGeneral();

  const dataDisplayVariant = useResponsiveDataDisplayVariant();

  const cashToClose = useSelector(selectFinancialInfoCashToClose);
  const projectedReserves = useSelector(selectFinancialInfoProjectedReserves);
  const dti = useSelector(selectFinancialInfoDebtToIncome);
  const summaryPendingFields = useSelector(selectFinancialInfoSummaryPendingFields);
  const totalIncomes = useSelector(selectFinancialInfoTotalIncomes({ borrowerId, pendingChanges }));
  const savedTotalIncomes = useSelector(
    selectFinancialInfoCurrentFinancialsBorrowerIncomes(borrowerId),
  );
  const totalAssets = useSelector(selectFinancialInfoTotalAssets({ borrowerId, pendingChanges }));
  const savedTotalAssets = useSelector(
    selectFinancialInfoCurrentFinancialsBorrowerAssets(borrowerId),
  );
  const totalLiabilities = useSelector(
    selectFinancialInfoTotalLiabilities({ borrowerId, pendingChanges }),
  );
  const savedTotalLiabilities = useSelector(
    selectFinancialInfoCurrentFinancialsBorrowerLiabilities(borrowerId),
  );
  const totalNegativeRentalIncome = useSelector(
    selectFinancialInfoTotalNegativeRentalIncome({ borrowerId }),
  );

  const { title, summaryCardsNames, addButtonText } = TITLE_DATA[tablePath];

  const summaryInfo = getSummaryInfoConfig({
    totalIncomes,
    totalAssets,
    totalLiabilities,
    totalNegativeRentalIncome,
    projectedReserves,
    cashToClose,
    dti,
    summaryPendingFields,
    isPersonalized: true,
    isModalContext: true,
    isIncomesChanged: savedTotalIncomes !== totalIncomes,
    isAssetsChanged: savedTotalAssets !== totalAssets,
    isLiabilitiesChanged: savedTotalLiabilities !== totalLiabilities,
  }).filter(card => summaryCardsNames.includes(card.name));

  return (
    <Box sx={titleWrapperStyles}>
      <Typography sx={titleStyles} variant="regular14">
        {title}
      </Typography>

      <Box
        sx={getSummaryWrapperStyles(SUMMARY_CARDS_COLUMNS)}
        onMouseLeave={onResetTooltipState}
        onMouseOver={onMouseOver}
      >
        <TruncatedProvider />

        {summaryInfo.map(({ name, value, icon, iconColor, changeKind }) => (
          <DataDisplay
            changeKind={changeKind}
            icon={icon}
            iconColor={iconColor}
            key={name}
            title={name}
            value={value}
            variant={dataDisplayVariant}
          />
        ))}
      </Box>

      <Button
        data-testid={AdditionalComponentsTestIds.ADD_RECORD_BUTTON}
        sx={addButtonStyles}
        variant="text"
        onClick={onAddNewRecord}
      >
        <AddRemoveIcon color={COLOR_PALETTE.BRAND[6]} />
        <Typography variant="regular14">Add {addButtonText}</Typography>
      </Button>
    </Box>
  );
};

Title.propTypes = {
  tablePath: PropTypes.string.isRequired,
  pendingChanges: PropTypes.object.isRequired,
  onAddNewRecord: PropTypes.func.isRequired,
};

export default Title;
